import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, Clock, Coins, Users2 } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { StatTile } from '@/components/StatTile';
import { to12h } from '@/services/appointments.service';
import { formatMetaInsightsValue } from '@/utils/currency';
import { useLabels } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { staffService, type StaffMember, type WeekSummary } from '@/services/staff.service';
import { mondayOfStr, ymdLocal } from './weekUtils';
import StaffDirectory from './StaffDirectory';
import ShiftRoster from './ShiftRoster';

type Tab = 'roster' | 'staff';

export default function StaffShifts() {
  const t = useLabels();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';

  const [tab, setTab] = useState<Tab>('roster');
  const [weekStart, setWeekStart] = useState(() => mondayOfStr(ymdLocal(new Date())));

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffError, setStaffError] = useState(false);
  const [addStaffRequested, setAddStaffRequested] = useState(false);
  const [summary, setSummary] = useState<WeekSummary | null>(null);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    setStaffError(false);
    try { setStaff((await staffService.listStaff()).items); }
    catch { setStaffError(true); } finally { setStaffLoading(false); }
  }, []);

  const loadSummary = useCallback(async () => {
    try { setSummary(await staffService.weekSummary(weekStart)); } catch { /* interceptor toasts */ }
  }, [weekStart]);

  useEffect(() => { void loadStaff(); }, [loadStaff]);
  useEffect(() => { void loadSummary(); }, [loadSummary]);

  const activeCount = staff.filter((s) => s.isActive).length;

  return (
    <div>
      <Topbar
        title={t('staff')}
        breadcrumb={`${t('operations')} / ${t('staff')}`}
        description="The weekly rota and your team's rates."
      />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <StatTile label="Active staff" value={staffLoading ? '—' : activeCount} icon={<Users2 size={16} />} accent="gold" />
          <StatTile label="Hours this week" value={summary ? summary.totals.hours : '—'} icon={<Clock size={16} />} accent="blue" />
          <StatTile
            label="Labour cost this week"
            value={summary ? formatMetaInsightsValue(String(summary.totals.cost), summary.currency) : '—'}
            icon={<Coins size={16} />}
            accent="green"
          />
          <StatTile label="On shift now" value={summary ? summary.onShiftNow.length : '—'} icon={<CalendarClock size={16} />} accent="amber" />
        </div>

        {summary && summary.onShiftNow.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface p-3 shadow-soft">
            <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">On shift now</span>
            {summary.onShiftNow.map((row) => (
              <span key={row.shiftUid} className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-2xs font-semibold text-success">
                {row.name} · {to12h(row.start)}–{to12h(row.end)}{row.station ? ` · ${row.station}` : ''}
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-1.5">
          {(['roster', 'staff'] as Tab[]).map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={clsx(
                'inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                tab === tb ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
            >
              {tb === 'roster' ? 'Weekly rota' : 'Team'}
            </button>
          ))}
        </div>

        {tab === 'roster' ? (
          <ShiftRoster
            weekStart={weekStart}
            onWeekStartChange={setWeekStart}
            staff={staff}
            staffLoading={staffLoading}
            staffError={staffError}
            onRetryStaff={loadStaff}
            onAddStaff={() => { setAddStaffRequested(true); setTab('staff'); }}
            isAdmin={isAdmin}
            onChanged={loadSummary}
          />
        ) : (
          <StaffDirectory
            staff={staff}
            loading={staffLoading}
            error={staffError}
            onRetry={loadStaff}
            isAdmin={isAdmin}
            onChanged={loadStaff}
            openNewForm={addStaffRequested}
            onNewFormOpened={() => setAddStaffRequested(false)}
          />
        )}
      </div>
    </div>
  );
}
